import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Briefcase, AlertCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { Holding } from '@/api/types';
import { useSSE, type SseEvent } from '@/hooks/useSSE';
import { krw, num, pct, pnlColor, ago } from '@/lib/format';
import { cn } from '@/lib/cn';
import { RiskChip } from '@/components/RiskChip';

export function Holdings() {
  const qc = useQueryClient();
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['holdings'],
    queryFn: dashboardApi.holdings,
    refetchInterval: 30_000,
  });

  // Merge live price ticks into the cached holdings — patch by instrumentCode,
  // recompute market value / unrealized P&L client-side so the table doesn't
  // wait on the 30s refetch.
  useSSE('market', {
    handlers: {
      'price.tick': (e: SseEvent<{ instrumentCode?: string; currentPrice?: number }>) => {
        const code = e.payload?.instrumentCode;
        const px = e.payload?.currentPrice;
        if (!code || px == null) return;
        qc.setQueryData<Holding[]>(['holdings'], (current) => {
          if (!current) return current;
          let touched = false;
          const next = current.map((h) => {
            if (h.code !== code) return h;
            touched = true;
            const marketValue = px * h.quantity;
            const unrealizedPnl = (px - h.avgBuyPrice) * h.quantity;
            const costBasis = h.avgBuyPrice * h.quantity;
            const unrealizedPnlPct = costBasis === 0 ? 0 : (unrealizedPnl * 100) / costBasis;
            return { ...h, currentPrice: px, marketValue, unrealizedPnl, unrealizedPnlPct };
          });
          return touched ? next : current;
        });
      },
    },
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Briefcase size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Holdings</h1>
          <div className="text-xs text-text-faint">
            {data ? `${data.length} position${data.length === 1 ? '' : 's'}` : '—'} ·
            {' '}updated {dataUpdatedAt ? ago(new Date(dataUpdatedAt).toISOString()) : '—'}
          </div>
        </div>
      </div>

      {isLoading && <SkeletonRows />}
      {isError && (
        <div className="card max-w-2xl flex items-center gap-3 text-kam-red">
          <AlertCircle size={18} />
          <div>
            <div className="font-semibold">Couldn't load holdings</div>
            <div className="text-xs text-text-dim">/api/holdings returned an error. Check the backend.</div>
          </div>
        </div>
      )}

      {data && data.length === 0 && (
        <div className="card max-w-2xl text-text-dim">No open positions.</div>
      )}

      {data && data.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block card !p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 border-b border-[color:var(--border)]">
                <tr className="text-left">
                  <Th>Code</Th>
                  <Th>Name</Th>
                  <Th right>Qty</Th>
                  <Th right>Avg cost</Th>
                  <Th right>Last</Th>
                  <Th right>MTM P&amp;L</Th>
                  <Th right>%</Th>
                  <Th right>Weight</Th>
                  <Th>Conditions</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((h) => (
                  <tr key={h.code} className="border-b border-[color:var(--border)] last:border-0 hover:bg-surface-2/60">
                    <Td className="font-mono">{h.code}</Td>
                    <Td>
                      <div className="font-medium">{h.name}</div>
                      <div className="text-[10px] text-text-faint uppercase tracking-wider">{h.sector ?? h.market}</div>
                    </Td>
                    <Td right className="font-mono">{num(h.quantity)}</Td>
                    <Td right className="font-mono text-text-dim">{num(h.avgBuyPrice)}</Td>
                    <Td right className="font-mono">{num(h.currentPrice)}</Td>
                    <Td right className={cn('font-mono', pnlColor(h.unrealizedPnl))}>{krw(h.unrealizedPnl, { sign: true })}</Td>
                    <Td right className={cn('font-mono', pnlColor(h.unrealizedPnlPct))}>{pct(h.unrealizedPnlPct)}</Td>
                    <Td right className="font-mono text-text-dim">{pct(h.weightPct, { sign: false })}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {h.conditions.map((c) => <RiskChip key={c.id} chip={c} />)}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden flex flex-col gap-3">
            {data.map((h) => (
              <div key={h.code} className="card card-tight">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{h.name}</div>
                    <div className="text-[11px] text-text-faint font-mono">{h.code} · {h.sector ?? h.market}</div>
                  </div>
                  <div className="text-right">
                    <div className={cn('font-mono text-base font-semibold', pnlColor(h.unrealizedPnl))}>
                      {krw(h.unrealizedPnl, { sign: true })}
                    </div>
                    <div className={cn('font-mono text-xs', pnlColor(h.unrealizedPnlPct))}>{pct(h.unrealizedPnlPct)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <Cell label="Qty" value={num(h.quantity)} />
                  <Cell label="Avg" value={num(h.avgBuyPrice)} />
                  <Cell label="Last" value={num(h.currentPrice)} />
                  <Cell label="Value" value={krw(h.marketValue)} />
                  <Cell label="Weight" value={pct(h.weightPct, { sign: false })} />
                  <Cell label="Conditions" value={String(h.conditions.length)} />
                </div>
                {h.conditions.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-[color:var(--border)]">
                    {h.conditions.map((c) => <RiskChip key={c.id} chip={c} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th className={cn('label py-2 px-3 font-semibold', right && 'text-right')}>{children}</th>
  );
}

function Td({ children, right, className }: { children: React.ReactNode; right?: boolean; className?: string }) {
  return (
    <td className={cn('py-2.5 px-3', right && 'text-right', className)}>{children}</td>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="font-mono text-text">{value}</div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="card card-tight animate-pulse">
          <div className="h-3 w-40 bg-surface-3 rounded mb-2" />
          <div className="h-2 w-24 bg-surface-3 rounded" />
        </div>
      ))}
    </div>
  );
}
